import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { useInvalidateCredits } from '@/features/dashboard/hooks/useCredits';
import type {
  Evaluation,
  EvaluationWithDetails,
  EvaluationCriterion,
  RunEvaluationInput,
  BatchEvaluationProgress,
  BucketResult,
  AIModel,
} from '@/features/evaluation/types';
import { getBucketCategory, DEFAULT_BUCKET_CONFIG } from '@/features/evaluation/types';
import { toast } from 'sonner';

const EVALUATIONS_KEY = 'evaluations';
const EVALUATION_KEY = 'evaluation';

// =========================================
// Queries
// =========================================

const EVALUATIONS_PAGE_SIZE = 50;

/** Fetch evaluations for the current user with pagination */
export function useEvaluations(options?: { source_id?: string; status?: string; page?: number }) {
  const user = useAuthStore((s) => s.user);
  const page = options?.page ?? 0;

  return useQuery({
    queryKey: [EVALUATIONS_KEY, user?.id, options],
    queryFn: async (): Promise<Evaluation[]> => {
      if (!user) return [];

      let query = supabase
        .from('evaluations')
        .select('*, sources(id, file_name, title)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(page * EVALUATIONS_PAGE_SIZE, (page + 1) * EVALUATIONS_PAGE_SIZE - 1);

      if (options?.source_id) {
        query = query.eq('source_id', options.source_id);
      }

      if (options?.status) {
        query = query.eq('status', options.status);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as Evaluation[];
    },
    enabled: !!user,
  });
}

/** Fetch a single evaluation with details */
export function useEvaluation(evaluationId: string | undefined) {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: [EVALUATION_KEY, evaluationId],
    queryFn: async (): Promise<EvaluationWithDetails | null> => {
      if (!evaluationId || !user) return null;

      // Fetch evaluation
      const { data: evaluation, error: evalError } = await supabase
        .from('evaluations')
        .select('*')
        .eq('id', evaluationId)
        .single();

      if (evalError) throw evalError;
      if (!evaluation) return null;

      // Fetch source info
      const { data: source } = await supabase
        .from('sources')
        .select('id, file_name, title, authors')
        .eq('id', evaluation.source_id)
        .single();

      // Fetch criteria results
      const { data: criteriaResults } = await supabase
        .from('evaluation_criteria')
        .select('*')
        .eq('evaluation_id', evaluationId);

      return {
        ...evaluation,
        source: source ?? undefined,
        criteria_results: (criteriaResults ?? []) as EvaluationCriterion[],
      } as EvaluationWithDetails;
    },
    enabled: !!evaluationId && !!user,
  });
}

// =========================================
// Mutations
// =========================================

/** Run evaluation for a single source against selected criteria */
export function useRunEvaluation() {
  const queryClient = useQueryClient();
  const invalidateCredits = useInvalidateCredits();

  return useMutation({
    mutationFn: async ({
      source_id,
      criteria_ids,
      ai_model,
    }: {
      source_id: string;
      criteria_ids: string[];
      ai_model: AIModel;
    }) => {
      return api.evaluateSource({ source_id, criteria_ids, ai_model });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVALUATIONS_KEY] });
      invalidateCredits();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

/** Run batch evaluation for multiple sources */
export function useRunBatchEvaluation() {
  const runEvaluation = useRunEvaluation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      source_ids,
      criteria_ids,
      ai_model,
      onProgress,
    }: RunEvaluationInput & {
      onProgress?: (progress: BatchEvaluationProgress) => void;
    }) => {
      const progress: BatchEvaluationProgress = {
        total: source_ids.length,
        completed: 0,
        failed: 0,
        current_source_id: null,
        results: [],
      };

      // Process in waves of 5 concurrent evaluations
      const WAVE_SIZE = 5;
      for (let i = 0; i < source_ids.length; i += WAVE_SIZE) {
        const wave = source_ids.slice(i, i + WAVE_SIZE);
        progress.current_source_id = wave[0] ?? null;
        onProgress?.(progress);

        const waveResults = await Promise.allSettled(
          wave.map((source_id) =>
            runEvaluation.mutateAsync({ source_id, criteria_ids, ai_model }),
          ),
        );

        for (const result of waveResults) {
          if (result.status === 'fulfilled') {
            progress.completed += 1;
            progress.results.push(result.value as EvaluationWithDetails);
          } else {
            progress.failed += 1;
          }
        }

        onProgress?.(progress);
      }

      progress.current_source_id = null;
      onProgress?.(progress);
      return progress;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVALUATIONS_KEY] });
    },
  });
}

/** Delete an evaluation */
export function useDeleteEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (evaluationId: string) => {
      const { error } = await supabase
        .from('evaluations')
        .delete()
        .eq('id', evaluationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVALUATIONS_KEY] });
      toast.success('Evaluation deleted');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

// =========================================
// Auto-bucketing
// =========================================

/** Assign evaluations to buckets (include/maybe/exclude) */
export function useBucketEvaluations() {
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: async ({
      evaluations,
      config,
    }: {
      evaluations: Evaluation[];
      config?: { include_threshold: number; exclude_threshold: number };
    }): Promise<BucketResult[]> => {
      if (!user) throw new Error('Not authenticated');

      const bucketConfig = config ?? DEFAULT_BUCKET_CONFIG;
      const results: BucketResult[] = [];

      // Create result folders if they don't exist
      const bucketNames = ['Include', 'Maybe', 'Exclude'] as const;
      const folderIds: Record<string, string> = {};

      for (const bucketName of bucketNames) {
        const { data: existing } = await supabase
          .from('folders')
          .select('id')
          .eq('user_id', user.id)
          .eq('name', `Results: ${bucketName}`)
          .eq('folder_type', 'result')
          .maybeSingle();

        if (existing) {
          folderIds[bucketName.toLowerCase()] = existing.id;
        } else {
          const { data: created } = await supabase
            .from('folders')
            .insert({
              user_id: user.id,
              name: `Results: ${bucketName}`,
              folder_type: 'result',
              color:
                bucketName === 'Include'
                  ? 'emerald'
                  : bucketName === 'Maybe'
                    ? 'amber'
                    : 'rose',
            })
            .select('id')
            .single();

          if (created) {
            folderIds[bucketName.toLowerCase()] = created.id;
          }
        }
      }

      // Assign each evaluation to a bucket
      for (const evaluation of evaluations) {
        if (evaluation.score === null) continue;

        const category = getBucketCategory(evaluation.score, bucketConfig);
        const folderId = folderIds[category] ?? null;

        // Link source to result folder
        if (folderId) {
          await supabase
            .from('folder_sources')
            .upsert(
              { folder_id: folderId, source_id: evaluation.source_id },
              { onConflict: 'folder_id,source_id' }
            );
        }

        results.push({
          category,
          source_id: evaluation.source_id,
          score: evaluation.score,
          folder_id: folderId,
        });
      }

      return results;
    },
    onSuccess: (results) => {
      const included = results.filter((r) => r.category === 'include').length;
      const maybe = results.filter((r) => r.category === 'maybe').length;
      const excluded = results.filter((r) => r.category === 'exclude').length;
      toast.success(
        `Bucketed: ${included} included, ${maybe} maybe, ${excluded} excluded`
      );
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
